import React from 'react';
import { getBezierPath, EdgeProps, useStore, BaseEdge, getSmoothStepPath, Position, MarkerType, EdgeMarker, useReactFlow } from '@xyflow/react';
import styles from './WorkflowEdge.module.css';
import { ERROR_HANDLE_ID, ERROR_ANGLE, ERROR_START_SHIFT } from '../utils/errorFlow';

/** 예외 흐름 선 — 30° 로 비스듬히 내려간 뒤 오른쪽으로 곧게 간다(모서리는 살짝 둥글게).
    정상 흐름의 직각 꺾임과 눈으로 구분되고, 아래로 내려가 다음 줄로 이어지는 흐름이 읽힌다. */
const BEND_R = 8;                       // 꺾이는 곳 둥글기

function errorEdgePath(sx: number, sy: number, tx: number, ty: number): [string, number, number] {
  const dy = ty - sy;
  const dx = tx - sx;
  // 타깃이 아래·오른쪽에 있을 때만 비스듬히 내린다 — 같은 높이거나 왼쪽이면 곧게 잇는다
  if (dy < 6 || dx < 24) return [`M ${sx},${sy} L ${tx},${ty}`, (sx + tx) / 2, (sy + ty) / 2];

  // 30° 로 타깃 높이까지 내려간 뒤 **수평으로** 들어간다. 가로 여유가 모자라면 그만큼 각도만 가팔라진다
  // (수직 구간을 만들면 노드에 수직으로 꽂혀 어색했다 — 현장 2026-09-23)
  const need = dy / Math.tan(ERROR_ANGLE);
  const bx = sx + Math.min(need, Math.max(dx - 24, 12));
  const slope = Math.hypot(bx - sx, dy);
  const r = Math.max(0, Math.min(BEND_R, slope / 2, (tx - bx) / 2));
  const p1 = { x: bx - ((bx - sx) / slope) * r, y: ty - (dy / slope) * r };
  const path = `M ${sx},${sy} L ${p1.x},${p1.y} Q ${bx},${ty} ${bx + r},${ty} L ${tx},${ty}`;
  return [path, (bx + r + tx) / 2, ty];      // + 는 수평 구간 가운데
}

/** 갈래가 공유하는 세로선(줄기)과 노드 사이 거리 */
const TRUNK_GAP = 28;

const WorkflowEdge = ({ 
  id, 
  source, 
  target, 
  sourceX, 
  sourceY, 
  targetX, 
  targetY, 
  sourcePosition = Position.Right,
  targetPosition = Position.Left,
  style, 
  markerEnd, 
  data, 
  interactionWidth, 
  selected,
  sourceHandleId,
}: EdgeProps) => {
  // 예외 흐름(데이터처리 실패 시 가는 길)은 전용 모양 — utils/errorFlow
  const isError = sourceHandleId === ERROR_HANDLE_ID;
  // 펼침·모음을 **줄기 하나**로 — 한 노드에서 여러 갈래로 나가는 선은 그 노드 바로 옆의 같은 세로선에서 갈라지고,
  // 여러 갈래가 한 노드로 모이는 선은 그 노드 바로 앞의 같은 세로선에서 합친다.
  // 예전에는 선마다 출발·도착의 가운데에서 꺾여, 노드의 가로 위치가 조금만 달라도 세로선이 여러 가닥으로 흩어졌다
  // (현장 2026-10-12: 파티션 8개 펼침·모음에서 세로선 16가닥).
  const fanOut = useStore((s: any) => s.edges.filter((e: any) => e.source === source && e.sourceHandle !== ERROR_HANDLE_ID).length);
  const fanIn = useStore((s: any) => s.edges.filter((e: any) => e.target === target && e.sourceHandle !== ERROR_HANDLE_ID).length);
  const forward = sourcePosition === Position.Right && targetPosition === Position.Left && targetX - sourceX > TRUNK_GAP * 2 + 8;
  const trunkX = !forward || isError ? undefined
    : fanOut > 1 ? sourceX + TRUNK_GAP
      : fanIn > 1 ? targetX - TRUNK_GAP
        : undefined;
  const [edgePath, labelX0, labelY0] = isError
    // 핸들의 오른쪽 끝이 아니라 **눈에 보이는 중앙**에서 선이 나가게 되돌린다 (utils/errorFlow)
    ? errorEdgePath(sourceX - ERROR_START_SHIFT, sourceY, targetX, targetY)
    : getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 10,
      ...(trunkX !== undefined ? { centerX: trunkX } : {}),
    });
  // + 는 줄기 위가 아니라 **그 선만 지나는 가로 구간** 가운데에 — 줄기에 두면 갈래들의 + 가 한 줄로 겹친다
  const [labelX, labelY] = trunkX === undefined ? [labelX0, labelY0]
    : fanOut > 1 ? [(trunkX + targetX) / 2, targetY]
      : [(sourceX + trunkX) / 2, sourceY];
  const edgeCenterX = labelX;
  const edgeCenterY = labelY;
  const nodes = useStore((state: any) => state.nodes);
  const targetNode = nodes.find((n: any) => n.id === target);
  // 분기/합류에 붙은 엣지에는 + 를 두지 않는다 (경로 의미가 제어 노드에 묶여 있어 중간 삽입 대상이 아님) — 캔버스가 data.noInsert 로 표시
  const showPlusButton = (!targetNode || targetNode.type !== 'placeholder') && !data?.noInsert;

  // 툴박스 드래그 중: + 를 크게 보여 주고, 커서가 반경 안이면 활성(드롭 가능) 표시
  const { flowToScreenPosition } = useReactFlow();
  const toolboxDragging = !!(data as any)?.toolboxDragging;
  const dragPos = (data as any)?.dragPosition as { x: number; y: number } | null | undefined;
  let dropActive = false;
  if (toolboxDragging && dragPos) {
    const sp = flowToScreenPosition({ x: edgeCenterX, y: edgeCenterY });
    dropActive = Math.hypot(sp.x - dragPos.x, sp.y - dragPos.y) <= 26;
  }
  const btn = dropActive ? 32 : toolboxDragging ? 26 : 20; // 버튼 한 변
  const arm = dropActive ? 9 : toolboxDragging ? 7 : 5;     // + 팔 길이
  
  // 선택된 상태를 스타일에 반영
  const violation = (data?.dfdViolation as string | undefined) || null;
  const edgeStyle = {
    ...style,
    stroke: selected ? '#ff3366' : violation ? '#f59e0b' : style?.stroke || '#888',
    strokeWidth: selected ? 3 : style?.strokeWidth || 2,
    // 넘겨받은 점선 설정을 지우지 않는다 — 예외 흐름(빨간 점선)이 실선으로 그려지던 까닭 (utils/errorFlow)
    strokeDasharray: violation ? '6 4' : (style?.strokeDasharray as string | undefined),
    transition: 'stroke 0.2s, stroke-width 0.2s',
  };

  // + 클릭 → 캔버스가 클릭 지점에 "삽입할 노드 유형" 메뉴를 띄운다
  const onClick = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    if (typeof data?.onInsertNode === 'function') data.onInsertNode({ x: evt.clientX, y: evt.clientY });
  };
  

  // 명시적으로 URL 형식으로 변환
  let markerUrl = undefined;
  
  // markerEnd가 직접 URL 문자열일 경우
  if (typeof markerEnd === 'string' && markerEnd.startsWith('url(#')) {
    markerUrl = markerEnd;
  } 
  // markerEnd가 MarkerType Enum 값일 경우
  else if (typeof markerEnd === 'string') {
    markerUrl = `url(#react-flow__${markerEnd})`;
  }
  // markerEnd가 EdgeMarker 객체일 경우
  else if (markerEnd && typeof markerEnd === 'object' && 'type' in markerEnd) {
    const edgeMarker = markerEnd as EdgeMarker;
    if (typeof edgeMarker.type === 'string') {
      markerUrl = `url(#react-flow__${edgeMarker.type})`;
    }
  }

  return (
    <>
      {/* 넓은 상호작용 영역 (투명 경로) */}
      <path
        id={`${id}-interaction`}
        style={{ ...style, strokeOpacity: 0, fill: 'none' }}
        className="react-flow__edge-interaction"
        d={edgePath}
        strokeWidth={interactionWidth || 25} // 더 넓게 설정
        pointerEvents="stroke"
      />
      
      {/* 시각적으로 보이는 경로 — fill 은 CSS 외에 속성으로도 none (이미지/PDF 내보내기(html-to-image)는 <svg> 안쪽에
          CSS 계산 스타일을 옮기지 않아 곡선 경로가 검게 채워졌음, 2026-09-05 실측. 속성·인라인 style 만 살아남는다) */}
      <path 
        id={id} 
        style={edgeStyle} 
        className={`${styles.edgePath} ${selected ? styles.selectedEdge : ''}`} 
        d={edgePath} 
        fill="none"
        markerEnd="url(#react-flow__arrowclosed)"
      />
      
      {/* + 버튼 — 편집용 장치라 이미지/PDF 내보내기에서는 뺀다 (data-export-skip) */}
      {showPlusButton && (
        <g
          transform={`translate(${edgeCenterX}, ${edgeCenterY})`}
          pointerEvents="all"
          data-export-skip="true"
          className={`${styles.edgeButtonGroup} ${isError ? styles.edgeButtonErrorGroup : ''} ${toolboxDragging ? styles.edgeButtonDropTarget : ''} ${dropActive ? styles.edgeButtonDropActive : ''}`}
        >
          {/* 넓은 드롭/클릭 판정 영역 — 보이지 않는 원 (드래그 중에는 더 크게) */}
          <circle className={styles.edgeHitArea} r={toolboxDragging ? 26 : 14} onClick={onClick} />
          {dropActive && <circle className={styles.edgeDropRing} r={22} />}
          <rect 
            onClick={onClick} 
            x={-btn / 2} y={-btn / 2} 
            width={btn} height={btn} 
            ry={5} rx={5} 
            className={styles.edgeButton} 
          />
          {/* + 기호는 글꼴의 베이스라인 영향 없이 정중앙에 오도록 선 두 개로 그린다 */}
          <line className={styles.edgePlus} x1={-arm} y1={0} x2={arm} y2={0} />
          <line className={styles.edgePlus} x1={0} y1={-arm} x2={0} y2={arm} />
        </g>
      )}
    </>
  );
};

export default WorkflowEdge; 